import React from "react";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { STATS, BRAND, PHOTOS } from "../lib/data";

export default function AboutPage() {
    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow="About"
                title={`Roots in the DMV since ${BRAND.established}.`}
                sub="A family-run landscape & hardscape studio with a quiet reputation for design-led craftsmanship and unflashy reliability."
                image={PHOTOS.lushFloweringGarden}
            />

            <section className="px-4 sm:px-8 py-12">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                            <img src={PHOTOS.poolPatioOutdoorLiving} alt="Outdoor space" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-base sm:text-lg text-brand-textMuted leading-relaxed">
                        <p>
                            Great Outdoors Landscaping was founded in 1998 with a small truck, a sharper eye than most, and a belief that an outdoor space should age into the property — not out of it. Twenty-five years later, that belief is still the brief on every project we touch.
                        </p>
                        <p>
                            We work across Arlington, Falls Church, McLean, Vienna, and the broader DMV — designing, building, and maintaining gardens, hardscapes, and full property landscapes for homeowners, HOAs, and commercial clients. The same family-run team you meet on day one is the team that finishes the job, and the team you call ten years later for a refresh.
                        </p>
                        <p>
                            We sit somewhere between a design studio and a construction crew. Our designers can talk soil chemistry; our masons can read a planting plan. That overlap is where the best projects happen — and it's why so many of our clients have been with us for a decade or more.
                        </p>
                        <div className="border-l-2 border-brand-accent pl-6 py-2 my-8">
                            <p className="font-serif text-2xl sm:text-3xl text-brand-primary leading-snug">
                                "We're not interested in the showroom shot — we're interested in how it looks in year five."
                            </p>
                            <p className="mt-3 text-sm uppercase tracking-[0.2em] text-brand-secondary">— Founding philosophy</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="px-4 sm:px-8 py-24 bg-brand-surfaceAlt">
                <div className="max-w-5xl mx-auto text-center">
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold mb-4">Our mission</p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-[1.1] font-medium tracking-tight">
                        Blending nature with functional design — so the outdoors becomes
                        the most-used room of the home.
                    </h2>
                </div>
            </section>

            {/* Stats */}
            <section className="px-4 sm:px-8 py-20">
                <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {STATS.map((s) => (
                        <div key={s.label} className="text-center sm:text-left border-l-2 border-brand-border pl-5">
                            <div className="font-serif text-4xl sm:text-5xl text-brand-primary font-semibold">{s.value}</div>
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-textMuted mt-2">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Values */}
            <section className="px-4 sm:px-8 py-20">
                <div className="max-w-7xl mx-auto">
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold mb-4">What we believe</p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-[1.1] font-medium tracking-tight max-w-3xl">
                        Four principles, applied stubbornly.
                    </h2>
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {[
                            ["Craft over speed", "We build it once. Properly. Materials, methods, and finishes are chosen to outlast the trend."],
                            ["Honest pricing", "Detailed estimates. No surprise change orders. If we don't think it's worth the spend, we'll say so."],
                            ["Single point of contact", "One project lead, start to finish — and one number for everything afterwards."],
                            ["Year-round care", "Cleanups, fertilisation, pruning, snow. The full calendar, handled."],
                        ].map(([t, d]) => (
                            <div key={t} data-testid={`about-value-${t}`} className="border-t border-brand-border pt-6">
                                <h3 className="font-serif text-2xl text-brand-primary font-semibold mb-3">{t}</h3>
                                <p className="text-sm text-brand-textMuted leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CTABanner
                eyebrow="Let's meet"
                title="Talk to the team that's still here in year ten."
                sub="A 30-minute on-site consultation is the best way to see what we'd do with your property."
                primaryLabel="Book a Consultation"
            />
        </div>
    );
}
